import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { Store } from '@ngrx/store';

import { ReplaySubject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { AppState, Selectors, Actions } from 'store/root-reducer';

import { Document } from 'modules/shared/models/document.models';
import { DocumentResponse } from 'store/models/document.models';
import { TableNameType, TablePreferenceType, TablesPreference, TableSortPreference } from 'modules/shared/models/table-preference.models';
import { RecordCrp } from 'modules/shared/models/crp.models';
import { RecordCrpResponse } from 'store/models/crp.models';

import { DocumentsService, IDocumentsRequest } from 'modules/core/services/documents.service';
import { CustomerService } from 'modules/core/services/customer.service';
import { SnackbarService } from 'app/services/snackbar.service';

@Component({
  selector: 'artemis-expiring-document-table',
  templateUrl: './expiring-document-table.component.html',
  styleUrls: ['./expiring-document-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpiringDocumentTableComponent implements OnInit, OnDestroy {

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.expiringDocumentsDataSource.sort = sort;
  }
  @ViewChild(MatPaginator, {static: true}) set paginator(paginator: MatPaginator) {
    this.expiringDocumentsDataSource.paginator = paginator;
  }

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  // Names should be same as used for sorting on the backend
  readonly displayedColumns: Array<string> = ['customer', 'document', 'doc_type', 'expiry_date', 'updated_at', 'actions'];

  readonly pageSizeOptions: Array<number> = [5, 10, 15];
  expiringDocumentsCount: number;
  preferredExpiringDocumentTablePageSize: number = this.pageSizeOptions[0];
  currentPage: number = 0;
  currentTableSort: string;

  isLoading: boolean = false;
  isActiveTableTab: string = 'document';

  expiringDocuments: Array<Document>;
  expiringDocumentsDataSource: MatTableDataSource<Document> = new MatTableDataSource([]);

  constructor(private documentsService: DocumentsService,
    private customersService: CustomerService,
    private snackBar: SnackbarService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private store: Store<AppState>) { }

  ngOnInit() {
    this.getExpiringDocumentTablePreference();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * Construct api request
   * @method getExpiringDocumentRequest
   */
  private getExpiringDocumentRequest(): IDocumentsRequest {
    return {
      limit: this.preferredExpiringDocumentTablePageSize,
      offset: this.preferredExpiringDocumentTablePageSize * this.currentPage,
      show_expiry_alerts: true,
      expiring: true,
      sort: this.currentTableSort
    };
  }

  /**
   * Get table preference from store
   * @method getExpiringDocumentTablePreference
   */
  private getExpiringDocumentTablePreference(): void {
    this.store.select(Selectors.userPreference.getTablesPreference)
      .pipe(
        takeUntil(this.unsubscribe$),
        take(1)
      )
      .subscribe((tablesPreference: TablesPreference) => {
        const tablePreference = tablesPreference[TableNameType.EXPIRING_DOCUMENT_TABLE];
        if (tablePreference) {
          if (tablePreference[TablePreferenceType.PAGE_SIZE]) {
            this.preferredExpiringDocumentTablePageSize = tablePreference[TablePreferenceType.PAGE_SIZE];
          }

          if (tablePreference[TablePreferenceType.SORT]) {
            switch (tablePreference[TablePreferenceType.SORT].direction) {
              case 'asc':
                this.currentTableSort = `${tablePreference[TablePreferenceType.SORT].active}`;
                break;

              case 'desc':
                this.currentTableSort = `-${tablePreference[TablePreferenceType.SORT].active}`;
                break;

              default:
                this.currentTableSort = undefined;
                break;
            }

            this.expiringDocumentsDataSource.sort.active = tablePreference[TablePreferenceType.SORT].active;
            this.expiringDocumentsDataSource.sort.direction = tablePreference[TablePreferenceType.SORT].direction;
          }
        }
        this.getExpiringDocuments();
      });
  }

  /**
   * Get page with expired documents
   * @method getExpiringDocuments
   */
  private getExpiringDocuments(sortPreference?: TableSortPreference): void {
    this.isLoading = true;
    this.cdRef.detectChanges();

    this.documentsService
      .getDocuments(this.getExpiringDocumentRequest())
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (documentsRes: DocumentResponse) => {
          this.expiringDocumentsCount = documentsRes.count;
          this.expiringDocuments = documentsRes.results;
          if (this.expiringDocumentsDataSource) {
            this.expiringDocumentsDataSource.data = this.expiringDocuments;
          } else {
            this.expiringDocumentsDataSource = new MatTableDataSource(this.expiringDocuments);
          }

          this.expiringDocumentsDataSource.paginator = this.paginator;

          this.isLoading = false;
          this.cdRef.detectChanges();
        },
        (error: any) => {
          this.isLoading = false;
          this.snackBar.error('Error: Failed to load list of expiring documents');
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Dismiss notification about expiring document
   * @method dismissNotification
   */
  dismissNotification(expiringDocument: Document): void {
    this.isLoading = true;

    this.documentsService
      .toggleNotification(expiringDocument.id, false)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (documentsRes: Document) => {
          this.snackBar.success('Expiring document notification dismissed');
          this.getExpiringDocuments();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to dismiss expiry document notification');
          this.isLoading = false;
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Pagination new page event handler
   * @method newTablePageHandler
   */
  newTablePageHandler(event: PageEvent): void {
    if (event.pageSize !== this.preferredExpiringDocumentTablePageSize) {
      this.store.dispatch(new Actions.userPreference.SetPaginationPreference({
        tableName: TableNameType.EXPIRING_DOCUMENT_TABLE,
        [TablePreferenceType.PAGE_SIZE]: event.pageSize
      }));

      this.preferredExpiringDocumentTablePageSize = event.pageSize;
    }
    this.currentPage = event.pageIndex;
    this.getExpiringDocuments();
  }

  sortExpiringDocs($event: Sort): void {
    this.store.dispatch(new Actions.userPreference.SetSortPreference({
      tableName: TableNameType.EXPIRING_DOCUMENT_TABLE,
      [TablePreferenceType.SORT]: <TableSortPreference>{
        active: $event.active,
        direction: $event.direction
      }
    }));

    switch ($event.direction) {
      case 'asc':
        this.currentTableSort = `${$event.active}`;
        break;

      case 'desc':
        this.currentTableSort = `-${$event.active}`;
        break;

      default:
        this.currentTableSort = undefined;
        break;
    }

    this.getExpiringDocuments();
  }

  /**
   * Gets info basing on crps about customer and navigate to the customer
   * @method navigateToCustomer
   */
  navigateToCustomer(expiringDocument: Document): void {
    this.isLoading = true;

    this.customersService
      .getRelatedCrp(expiringDocument.record.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (crpInfo: RecordCrpResponse) => {
          const crp: RecordCrp = crpInfo.results.find(record => record.record === expiringDocument.record.id);
          if (!crp) {
            this.isLoading = false;
            this.snackBar.error('Unable to find any CRP related to this document');
            return;
          }
          this.isLoading = false;
          if (crp.primary) {
            this.router.navigateByUrl(`/customers/${crp.customer.id}/edit`);
          } else {
            this.router.navigateByUrl(`/customers/${crp.customer.id}/edit/crp-edit/${crp.id}`);
          }
        },
        (error: any) => {
          this.snackBar.error(`Unable to find ${expiringDocument.record.name} in our database`);
          this.isLoading = false;
          this.cdRef.detectChanges();
        }
      );
  }
}
